import { useState } from "react"
import { CARD_BACK_LABELS, cardFace, cardNumber } from "@/content/cards"
import { CARDS_COPY } from "@/content/cardsRound"
import { cardTint } from "./cardTint"
import type { CardId } from "@/types"
import styles from "@/styles/components/CardFace.module.css"

/*
 * One card, front or back.
 *
 * The whole deck is drawn by this component: the deal, the spread, the board's
 * focus slot, the deep-dive header, the browser and the report. That is
 * deliberate — a card the room recognises on the board has to be the same object
 * it chose from the deck, and two implementations would drift.
 *
 * The face carries no illustration, and does not need one. The printed deck's
 * fronts are a family gradient with grain, a soft bloom in the lower corner and a
 * gloss along the top edge — no picture — so that treatment is reproduced in CSS
 * here (see `CardFace.module.css`) and the type stays live: selectable,
 * reflowable, and set in the product's own face rather than baked into an image.
 *
 * The family tint IS the identity: seven grounds, each with its own ink, so a
 * family is readable across a table before a word is.
 *
 * `statement` is set large because it is what the room argues about; `name` is
 * the short handle and rides above it as an eyebrow. The back is the three lines
 * that decide whether a card survives — `scope` is deliberately not among them,
 * because it is the facilitator's promise in the leave-behind, not a pitch to
 * choose by.
 */

interface CardFaceProps {
  cardId: CardId
  side?: "front" | "back"
  size?: "sm" | "md" | "lg"
  /** Given only when the card can be turned over. Absent means no affordance. */
  onFlip?: () => void
  className?: string
}

export default function CardFace({
  cardId,
  side = "front",
  size = "md",
  onFlip,
  className,
}: CardFaceProps) {
  const face = cardFace(cardId)
  const { tint, ink, bloom, family, short } = cardTint(cardId)

  const classes = [styles.card, styles[size], className].filter(Boolean).join(" ")

  /*
   * Both turns animate, but a card sitting front side up because it has never
   * been turned must not: every card mounts on its front, so without this the
   * deal, the spread and the board would all flip themselves in on load.
   *
   * `side` is a prop here and lives in a different place on every screen that
   * uses it, so what is tracked is the CHANGE, not who owns it — React's
   * adjust-state-during-render pattern, which re-renders before the browser
   * paints. An effect would be a frame late and the turn back would flash its
   * end state first.
   */
  const [seenSide, setSeenSide] = useState(side)
  const [turned, setTurned] = useState(false)
  if (seenSide !== side) {
    setSeenSide(side)
    setTurned(true)
  }

  return (
    <article
      className={classes}
      data-family={family}
      data-side={side}
      data-turned={turned ? "" : undefined}
      style={
        {
          // The ground is built from these two in CSS — a gradient, a bloom, the
          // grain and the gloss — rather than being a flat fill set here.
          "--card-tint": tint,
          "--card-bloom": bloom,
          color: ink,
        } as React.CSSProperties
      }
    >
      <p className={styles.eyebrow}>
        <span>{short}</span>
        <span className={styles.number}>{cardNumber(cardId)}</span>
      </p>

      {side === "front" ? (
        <>
          <p className={styles.name}>{face?.name}</p>
          <h3 className={styles.statement}>{face?.statement}</h3>
        </>
      ) : (
        <>
          <p className={styles.name}>{face?.name}</p>
          <dl className={styles.facts}>
            <div className={styles.fact}>
              <dt>{CARD_BACK_LABELS.looksLike}</dt>
              <dd>{face?.looksLike}</dd>
            </div>
            <div className={styles.fact}>
              <dt>{CARD_BACK_LABELS.needs}</dt>
              <dd>{face?.needs}</dd>
            </div>
            <div className={styles.fact}>
              <dt>{CARD_BACK_LABELS.watchOut}</dt>
              <dd>{face?.watchOut}</dd>
            </div>
          </dl>
        </>
      )}

      {onFlip ? (
        <button
          type="button"
          className={styles.flip}
          title={side === "front" ? CARDS_COPY.flipToBack : CARDS_COPY.flipToFront}
          aria-label={side === "front" ? CARDS_COPY.flipToBack : CARDS_COPY.flipToFront}
          onClick={onFlip}
        >
          {/* A turn-over arrow, not a chevron: the card rotates, it does not open. */}
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M2.5 6.5a5.5 5.5 0 0 1 9.4-2.6L13.5 5.5M13.5 9.5a5.5 5.5 0 0 1-9.4 2.6L2.5 10.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.5 2.5v3h-3M2.5 13.5v-3h3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
    </article>
  )
}
